// The calendar's date arithmetic, the same on the server and in the app. Days are plain calendar dates
// ("2026-10-18"), with no time or time zone: a birthday is the same day wherever you are.

export const DAY_RE = /^\d{4}-\d{2}-\d{2}$/
// The most days one request covers: a month view with the weeks around it, or Home's month ahead.
export const MAX_DAYS = 62

const pad = (n: number) => String(n).padStart(2, '0')
const iso = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
const leap = (year: number) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0

// A real day, written out in full; "2026-02-30" isn't one.
export function isDay(value: string) {
  if (!DAY_RE.test(value)) return false
  const d = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(d.getTime()) && iso(d) === value
}

// The days from `from`, `days` of them, in order.
export function dayRange(from: string, days: number): string[] {
  const start = new Date(`${from}T00:00:00Z`).getTime()
  return Array.from({ length: days }, (_, i) => iso(new Date(start + i * 86_400_000)))
}

// When a full date comes round again within these days, and how many years it has been each time. A partial date
// ("1958" or "1958-06") never lands on a day. Someone born on February 29 is marked on February 28 in other years,
// and `leapDay` says so. The date itself, and anything before it, isn't a return: year one is the first.
export function returns(
  date: string | null | undefined,
  range: string[],
): { on: string; years: number; leapDay: boolean }[] {
  if (!date || !isDay(date)) return []
  const year = Number(date.slice(0, 4))
  const monthDay = date.slice(5)
  const found: { on: string; years: number; leapDay: boolean }[] = []
  for (const on of range) {
    const y = Number(on.slice(0, 4))
    if (y <= year) continue
    const md = on.slice(5)
    if (md === monthDay) found.push({ on, years: y - year, leapDay: false })
    else if (monthDay === '02-29' && md === '02-28' && !leap(y)) found.push({ on, years: y - year, leapDay: true })
  }
  return found
}

// ---- Dates people add themselves

export const REPEATS = ['none', 'daily', 'weekly', 'monthly', 'yearly'] as const
export type Repeats = (typeof REPEATS)[number]
// A time on the wall clock where it happens, "09:30" or "18:00".
export const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/
// The most days one date can run over, by how often it comes round: never so long it runs into its own next time.
export const MAX_SPAN: Record<Repeats, number> = { none: 31, daily: 1, weekly: 7, monthly: 28, yearly: 31 }

export type DateRule = {
  startsOn: string
  endsOn?: string | null
  startsAt?: string | null
  endsAt?: string | null
  repeats: Repeats
  repeatsUntil?: string | null
}

const dayNumber = (day: string) => Date.parse(`${day}T00:00:00Z`) / 86_400_000
const dayAt = (n: number) => iso(new Date(n * 86_400_000))

// How many days it runs over: one, unless it ends on a later day.
export const spanOf = (rule: Pick<DateRule, 'startsOn' | 'endsOn'>) =>
  rule.endsOn ? dayNumber(rule.endsOn) - dayNumber(rule.startsOn) + 1 : 1

// What's wrong with a date as asked for, in words to show, or null when it's fine. The same check on the server and
// in the form.
export function dateProblem(rule: DateRule): string | null {
  if (!isDay(rule.startsOn)) return 'Pick the day it’s on.'
  if (rule.endsOn && !isDay(rule.endsOn)) return 'Pick the day it ends.'
  if (rule.startsAt && !TIME_RE.test(rule.startsAt)) return 'That isn’t a time.'
  if (rule.endsAt && !TIME_RE.test(rule.endsAt)) return 'That isn’t a time.'
  if (rule.endsAt && !rule.startsAt) return 'Give the time it starts too.'
  if (!REPEATS.includes(rule.repeats)) return 'Pick how often it comes round.'
  const span = spanOf(rule)
  if (span < 1) return 'It can’t end before it starts.'
  if (span === 1 && rule.startsAt && rule.endsAt && rule.endsAt <= rule.startsAt)
    return 'It can’t end before it starts.'
  if (span > MAX_SPAN[rule.repeats]) {
    return rule.repeats === 'none'
      ? `A date can run over ${MAX_SPAN.none} days at most.`
      : 'It runs into the next time it comes round. Make it shorter, or repeat it less often.'
  }
  if (rule.repeatsUntil) {
    if (rule.repeats === 'none') return 'Only a date that repeats can stop repeating.'
    if (!isDay(rule.repeatsUntil)) return 'Pick the day it stops repeating.'
    if (rule.repeatsUntil < rule.startsOn) return 'It can’t stop repeating before it starts.'
  }
  return null
}

// Whether a time it comes round starts on this day. A monthly date on the 31st skips the months without one, as other
// calendars do; a yearly one on February 29 is marked on the 28th in other years, as birthdays are.
function startsHere(rule: DateRule, day: string) {
  if (day < rule.startsOn || (rule.repeatsUntil && day > rule.repeatsUntil)) return false
  const [, sm, sd] = rule.startsOn.split('-')
  const [y, m, d] = day.split('-')
  switch (rule.repeats) {
    case 'none':
      return day === rule.startsOn
    case 'daily':
      return true
    case 'weekly':
      return (dayNumber(day) - dayNumber(rule.startsOn)) % 7 === 0
    case 'monthly':
      return d === sd
    case 'yearly':
      return (m === sm && d === sd) || (sm === '02' && sd === '29' && m === '02' && d === '28' && !leap(Number(y)))
  }
}

// Each day in the range a date falls on, which time round that is (the day it started), and which of its days this is.
export function occurrences(
  rule: DateRule,
  range: string[],
): { on: string; started: string; dayOf: number; days: number }[] {
  if (dateProblem(rule)) return []
  const days = spanOf(rule)
  const found: { on: string; started: string; dayOf: number; days: number }[] = []
  for (const on of range) {
    for (let back = 0; back < days; back++) {
      const started = dayAt(dayNumber(on) - back)
      if (startsHere(rule, started)) {
        found.push({ on, started, dayOf: back + 1, days })
        break
      }
    }
  }
  return found
}
